<script lang="ts" setup>
import SinglePageLayout from '@panel/layouts/Layout.SinglePage.vue';
import { GpBtnLink, GpTable, GpTableColumn, type GpTableDataRow } from '@gportal/uikit';
import useViewTitle from '@panel/composables/useViewTitle';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { computed, onMounted, reactive, ref } from 'vue';
import useGpTableData from '@panel/composables/useGpTableData';
import { type Project, ProjectMemberRole } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import type { BasicUser } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import type { ServiceError } from '@grpc/types';
import type { ListProjectsResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import Toolbar from 'gpcore-uikit/components/Component.Toolbar.vue';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import TableUser from 'gpcore-uikit/components/Component.Table.User.vue';
import TableProject from 'gpcore-uikit/components/Component.Table.Project.vue';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';

useViewTitle('List projects');

interface ProjectsTableRow extends GpTableDataRow {
  id: string;
  name: string;
  avatarUrl: string;
  owner: BasicUser;
  billingProfileName: string;
  usage?: string;
  isDefaultProject: boolean;
}

const projectStore = useProjectStore();
const sessionStore = useSessionStore();
const pagination = reactive({ currentPage: 0, totalPages: 1 });
const projects = ref<Array<Project & { usage?: string }>>([]);
const session = computed(() => sessionStore.session);
const isLoading = ref(false);
const { addNotification } = useNotifications();

const { tableData } = useGpTableData(projects, (data) =>
  data.map((project) => ({
    key: project.id,
    id: project.id,
    name: project.name,
    avatarUrl: project.avatarUrl,
    owner:
      project.members.filter((member) => member.role === ProjectMemberRole.OWNER).pop()?.user || {},
    billingProfileName: project.billingProfile?.name ?? '-',
    usage: project.usage,
    isDefaultProject: project.id === projectStore.defaultProjectId,
  })),
);

const loadProjects = async (page = 1) => {
  pagination.currentPage = page;

  try {
    const response = await projectStore.reloadProjects(page);
    pagination.totalPages = response.pagination.total;
    return response;
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  }

  return { projects: [] } as ListProjectsResponse;
};

// Add and format total usage amount to the table data
const fetchUsage = async (projectId: string) => {
  try {
    const usage = await projectStore.getUsage(projectId);
    // Only project owners can see the total usage. It the use is not the
    // project owner, "total" is not set.
    if (!usage.total) {
      return '-';
    }

    return usage.total.formatted;
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  }
};

const reload = async (page = 1) => {
  isLoading.value = true;
  try {
    const responseProjects = await loadProjects(page);
    const usagePromises = responseProjects.projects.map((project) => {
      return fetchUsage(project.id);
    });
    const usages = await Promise.all(usagePromises);
    projects.value = responseProjects.projects.map((project, index) => {
      const projectWithUsage: Project & { usage?: string } = project;
      projectWithUsage.usage = usages[index];
      return projectWithUsage;
    });
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  reload();
});
</script>

<template>
  <SinglePageLayout>
    <template #toolbar>
      <Toolbar title="Projects">
        <template #toolbarMainAction>
          <router-link :to="{ name: 'ProjectCreate' }" v-slot="{ href, navigate }" custom>
            <GpBtnLink
              :href="href"
              size="sm"
              :icon="['fas', 'fa-plus']"
              label="Create"
              @click="navigate"
            />
          </router-link>
        </template>
      </Toolbar>
    </template>
    <template #content>
      <p class="tw-mb-4">
        Use projects to group list of nodes that meet different requirements. Each project can have
        its own billing profile and usage statistics. Further, each project can have fine grained
        access control by adding members with different roles to it. Create as much projects as you
        like, assign a billing profile to it and grant permissions to other users. A separate audit
        log will be kept for each project. See
        <ExternalLink :url="`${$config.docs.url}/guides/project/`">the documentation</ExternalLink>
        on how to work with projects.
      </p>
      <GpTable
        :data="tableData"
        empty-message="No projects."
        pagination
        :page="pagination.currentPage"
        :max-pages="pagination.totalPages"
        :is-loading="isLoading"
        loading-text="Loading projects ..."
        @update:page="reload"
      >
        <GpTableColumn field="name" label="Name">
          <template #body="{ row }: { row: ProjectsTableRow }">
            <TableProject
              :avatar-url="row.avatarUrl"
              :name="row.name"
              :is-default-project="row.isDefaultProject"
              :size="'medium'"
              :id="row.id"
            />
          </template>
        </GpTableColumn>
        <GpTableColumn field="owner" label="Owner">
          <template #body="{ data }: { data: ProjectsTableRow['owner'] }">
            <div class="tw-flex tw-items-center tw-gap-2">
              <TableUser
                :user="{
                  avatarUrl: data.avatarUrl,
                  id: data.id,
                  fullName: data.fullName,
                  username: data.username,
                  email: data.email,
                }"
                :is-session-user="data.id === session.user?.id"
                avatar-size="md"
              />
            </div>
          </template>
        </GpTableColumn>
        <GpTableColumn field="billingProfileName" label="Billing profile">
          <template #body="{ data }: { data: ProjectsTableRow['billingProfileName'] }">
            {{ data }}
          </template>
        </GpTableColumn>
        <GpTableColumn field="usage" label="Usage">
          <template #body="{ data }: { data: ProjectsTableRow['usage'] }">
            {{ data }}
          </template>
        </GpTableColumn>
      </GpTable>
    </template>
  </SinglePageLayout>
</template>
